import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { PlaybookFlowStep } from '@/types/playbook/playbook';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface PlaybookFlowStepsProps {
  steps: PlaybookFlowStep[];
  title?: string;
  subtitle?: string;
}

const stepColors: Record<string, { bg: string; text: string }> = {
  genie: { bg: 'bg-[#4E9CFF]', text: 'text-white' },
  user: { bg: 'bg-[#28406F]', text: 'text-white' },
  action: { bg: 'bg-[#FF4DB8]', text: 'text-white' },
  result: { bg: 'bg-[#FFBB33]', text: 'text-[#1A1A1A]' },
};

export const PlaybookFlowSteps = ({
  steps,
  title = 'How it works',
  subtitle = 'Your voice genie follows this proven conversation flow to achieve results',
}: PlaybookFlowStepsProps) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  if (!steps || steps.length === 0) return null;

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-gray-50 relative overflow-hidden">
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="h-1 w-16 bg-gradient-to-r from-primary to-secondary rounded-full mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-lg md:text-xl text-gray-500 max-w-3xl mx-auto">{subtitle}</p>
        </div>

        {/* Steps - horizontal on desktop, vertical on mobile */}
        <div className="flex flex-col lg:flex-row items-center lg:items-start justify-center gap-6 lg:gap-0">
          {steps.map((step, idx) => {
            const StepIcon = step.icon;
            const colors = stepColors[step.type] || stepColors.genie;

            return (
              <div key={idx} className="flex flex-col lg:flex-row items-center">
                {/* Step */}
                <div
                  className={cn(
                    'text-center transition-all duration-700 ease-out',
                    isInView
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-8'
                  )}
                  style={{ transitionDelay: `${idx * 150}ms` }}
                >
                  {/* Circle with icon */}
                  <div className="relative mx-auto">
                    <div
                      className={cn(
                        'w-20 h-20 lg:w-24 lg:h-24 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110',
                        colors.bg,
                        colors.text
                      )}
                    >
                      {step.genieIcon ? (
                        <GenieIcon
                          name={step.genieIcon}
                          size={40}
                          color={step.type === 'result' ? '#1A1A1A' : '#FFFFFF'}
                        />
                      ) : (
                        <StepIcon className="h-8 w-8 lg:h-10 lg:w-10" />
                      )}
                    </div>
                    {/* Step number badge */}
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md border-2 border-gray-200">
                      <span className="text-xs font-bold text-gray-700">{idx + 1}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="max-w-[180px] mx-auto mt-4">
                    <h3 className="font-semibold text-gray-900 mb-1 text-sm lg:text-base">{step.label}</h3>
                    <p className="text-xs text-gray-500">{step.description}</p>
                    {step.duration && (
                      <p className="text-xs text-gray-400 mt-1">{step.duration}</p>
                    )}
                  </div>
                </div>

                {/* Connector line */}
                {idx < steps.length - 1 && (
                  <>
                    {/* Desktop horizontal connector */}
                    <div className="hidden lg:flex items-center mx-4 mt-[-40px]">
                      <div
                        className={cn(
                          'w-12 xl:w-16 h-0.5 bg-gradient-to-r from-gray-300 to-gray-400 transition-all duration-500',
                          isInView ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
                        )}
                        style={{ transitionDelay: `${idx * 150 + 75}ms` }}
                      />
                      <div
                        className={cn(
                          'w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[8px] border-l-gray-400 transition-opacity duration-500',
                          isInView ? 'opacity-100' : 'opacity-0'
                        )}
                        style={{ transitionDelay: `${idx * 150 + 100}ms` }}
                      />
                    </div>
                    {/* Mobile vertical connector */}
                    <div className="lg:hidden flex flex-col items-center my-2">
                      <div className="w-0.5 h-8 bg-gray-300" />
                      <div className="w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-t-[8px] border-t-gray-400" />
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
